import {
  materialTestKey,
  validateMaterialsInvestigationConfig,
} from '../minigames/materials/MaterialsInvestigationConfig.js';
import { MATERIALS_WORKSHOP_PRODUCTION_READY } from './MaterialsWorkshopAssets.js';

export const MATERIALS_WORKSHOP_ROOF_1 = validateMaterialsInvestigationConfig({
  id: 'materials-workshop-roof-1',
  masteryKey: 'science.material-properties.choose-for-purpose',
  title: 'A Roof Before the Rain',
  task: {
    purpose: 'observation-shelter-roof',
    problem:
      'The little field observation shelter needs a new roof panel before the rain arrives.',
    requiredProperties: ['water-resistant', 'rigid'],
    requiredObservationKeys: [
      materialTestKey('fiber-sheet', 'water-test'),
      materialTestKey('sealed-wood-panel', 'water-test'),
      materialTestKey('clear-polymer-sheet', 'water-test'),
      materialTestKey('sealed-wood-panel', 'bend-test'),
    ],
    buildResult: 'The sealed panel becomes a sturdy, water-resistant shelter roof.',
  },
  materials: [
    {
      id: 'fiber-sheet',
      label: 'Woven Fiber Sheet',
      description: 'A soft, cloth-like sheet with a slightly rough surface.',
      properties: ['flexible', 'absorbent', 'rough', 'opaque'],
    },
    {
      id: 'sealed-wood-panel',
      label: 'Sealed Wood Panel',
      description: 'A firm panel with a smooth protective coating.',
      properties: ['rigid', 'water-resistant', 'smooth', 'opaque', 'strong'],
    },
    {
      id: 'clear-polymer-sheet',
      label: 'Clear Polymer Sheet',
      description: 'A smooth, clear sheet that can flex when it is not supported.',
      properties: ['flexible', 'water-resistant', 'smooth', 'transparent'],
    },
  ],
  tests: [
    {
      id: 'water-test',
      label: 'Water Model',
      modelDescription: 'Animated drops show whether water soaks in or beads and runs away.',
      propertyOptions: ['absorbent', 'water-resistant'],
      observations: {
        'fiber-sheet': {
          evidenceId: 'fiber-sheet-absorbs-water',
          propertyId: 'absorbent',
          observation: 'The woven fiber sheet soaks up the modeled water and becomes wet.',
        },
        'sealed-wood-panel': {
          evidenceId: 'sealed-panel-resists-water',
          propertyId: 'water-resistant',
          observation: 'The modeled water beads on the sealed panel and runs off.',
        },
        'clear-polymer-sheet': {
          evidenceId: 'polymer-sheet-resists-water',
          propertyId: 'water-resistant',
          observation: 'The modeled water stays on the clear sheet instead of soaking in.',
        },
      },
    },
    {
      id: 'bend-test',
      label: 'Flex Model',
      modelDescription: 'A gentle in-game motion shows whether the sample stays rigid or flexes.',
      propertyOptions: ['rigid', 'flexible'],
      observations: {
        'fiber-sheet': {
          evidenceId: 'fiber-sheet-flexes',
          propertyId: 'flexible',
          observation: 'The woven fiber sheet bends and folds easily in the model.',
        },
        'sealed-wood-panel': {
          evidenceId: 'sealed-panel-stays-rigid',
          propertyId: 'rigid',
          observation: 'The sealed panel keeps its shape during the gentle flex model.',
        },
        'clear-polymer-sheet': {
          evidenceId: 'polymer-sheet-flexes',
          propertyId: 'flexible',
          observation: 'The clear sheet curves when the model presses its edges.',
        },
      },
    },
    {
      id: 'light-test',
      label: 'Light Model',
      modelDescription: 'A safe animated glow shows whether light passes through the sample.',
      propertyOptions: ['transparent', 'opaque'],
      observations: {
        'fiber-sheet': {
          evidenceId: 'fiber-sheet-blocks-light',
          propertyId: 'opaque',
          observation: 'The woven fiber sheet blocks the animated light.',
        },
        'sealed-wood-panel': {
          evidenceId: 'sealed-panel-blocks-light',
          propertyId: 'opaque',
          observation: 'The sealed panel blocks the animated light.',
        },
        'clear-polymer-sheet': {
          evidenceId: 'polymer-sheet-passes-light',
          propertyId: 'transparent',
          observation: 'The animated light can be seen through the clear sheet.',
        },
      },
    },
  ],
  minimumMaterialsInspected: 3,
  predictionPrompt: 'Which sample do you predict will make the most useful roof panel?',
  comparisonPrompt:
    'Compare what happened in the water model, then check which sample also kept its shape.',
  choicePrompt: 'Choose the material whose tested properties fit the shelter roof.',
  bestMaterialId: 'sealed-wood-panel',
  choiceFeedback: [
    {
      materialId: 'fiber-sheet',
      message:
        'That material soaked up the modeled water. What property would be better for a roof?',
    },
    {
      materialId: 'clear-polymer-sheet',
      message:
        'That sheet kept water out, but it flexed in the model. Which tested material also stayed rigid?',
    },
  ],
  successMessage:
    'The evidence supports the sealed panel: water runs off, and the panel stays rigid.',
  conclusionId: 'rigid-water-resistant-material-fits-roof-purpose',
  completionWorldEvent: 'shelter-roof-restored',
  hints: [
    'Think about what a roof needs to do.',
    'Which test tells us what happens when the material gets wet?',
    'Compare the materials after the water test.',
  ],
});

export const MATERIALS_WORKSHOP_SLICE = {
  id: 'materials-workshop',
  title: 'Materials Workshop',
  prerequisiteInvestigationId: 'pollinator-garden-1',
  productionReady: MATERIALS_WORKSHOP_PRODUCTION_READY,
  investigation: MATERIALS_WORKSHOP_ROOF_1,
} as const;
